/**
 * GameMenuScreen - Écran du menu de jeu avec image d'aventure, 3 slots, crédits, hall of fame
 * Identique à Flutter BaseMenuWidget
 */

import React, {useEffect, useState, useRef} from 'react';
import {
  View,
  Text,
  StyleSheet,
  Image,
  TouchableOpacity,
  SafeAreaView,
  Platform,
  ActivityIndicator,
  ScrollView,
  Animated,
  Easing,
  Dimensions,
  Modal,
} from 'react-native';
import {useNavigation, useRoute, RouteProp} from '@react-navigation/native';
import {FontAwesomeIcon} from '@fortawesome/react-native-fontawesome';
import {
  faBook,
  faScroll,
  faTrophy,
  faBolt,
  faShoppingBag,
  faStar,
  faTimes,
  faUserCircle,
} from '@fortawesome/free-solid-svg-icons';
import {usePlayer} from '../contexts/PlayerContext';
import gameStateService from '../services/gameStateService';
import adventureService from '../services/adventureService';
import {GameState} from '../models/GameState';
import {PlayerAdventure} from '../models/PlayerAdventure';
import {Fonts} from '../config/fonts';
import GameRegistry from '../systems/GameRegistry';
import {Alert} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';

interface GameMenuScreenParams {
  adventureURI?: string;
  adventureName?: string;
}

interface SlotState {
  hasSave: boolean;
  gameState: GameState | null;
  dateStr: string;
}

type GameMenuScreenRouteProp = RouteProp<{GameMenu: GameMenuScreenParams}, 'GameMenu'>;

const GameMenuScreen: React.FC = () => {
  const navigation = useNavigation();
  const route = useRoute<GameMenuScreenRouteProp>();
  const params = (route.params || {}) as GameMenuScreenParams;
  const {currentAdventure, initGame, startSlot, resetGameState, isContextReady} = usePlayer();
  const insets = useSafeAreaInsets();
  const [localAdventure, setLocalAdventure] = useState<PlayerAdventure | null>(null);
  const [imageData, setImageData] = useState<string | null>(null);
  const [slots, setSlots] = useState<SlotState[]>([]);
  const [loading, setLoading] = useState(true);
  const [hoveredSlotIndex, setHoveredSlotIndex] = useState<number | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [deleteConfirmationSlot, setDeleteConfirmationSlot] = useState<number | null>(null);
  const [pendingNavigation, setPendingNavigation] = useState<{adventureURI: string; slot: number} | null>(null);
  
  // Animation "porte de garage" qui descend
  // Commencer avec translateY à -height pour qu'il soit en haut et descende avec l'animation
  // Opacity commence à 1 pour que le contenu soit visible, mais l'overlay commence transparent
  const garageDoorAnim = useRef(new Animated.Value(-Dimensions.get('window').height)).current;
  const garageDoorOpacity = useRef(new Animated.Value(1)).current; // Menu visible dès le début
  const overlayOpacity = useRef(new Animated.Value(0)).current; // Overlay commence transparent pour voir la liste

  useEffect(() => {
    // Réinitialiser le gameState et currentStep quand on arrive sur le menu
    // pour s'assurer que le menu s'affiche correctement si on navigue vers PlayScreen
    resetGameState();
    
    if (params.adventureURI) {
      loadMenuData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.adventureURI]);

  // Naviguer vers PlayScreen quand le contexte est prêt et qu'une navigation est en attente
  useEffect(() => {
    if (pendingNavigation && isContextReady) {
      console.log(`[GameMenuScreen] Context ready, navigating to PlayScreen with slot ${pendingNavigation.slot}`);
      navigation.navigate('Play' as never, {
        adventureURI: pendingNavigation.adventureURI,
        slot: pendingNavigation.slot,
      } as never);
      setPendingNavigation(null); // Réinitialiser après navigation
    }
  }, [pendingNavigation, isContextReady, navigation]);

  // Mettre à jour l'image quand l'aventure change (localAdventure ou currentAdventure)
  useEffect(() => {
    const adventure = localAdventure || currentAdventure;
    if (adventure?.image) {
      let image = adventure.image;
      // Corriger le format si nécessaire
      if (!image.startsWith('data:image')) {
        image = `data:image/png;base64,${image}`;
      }
      console.log(`[GameMenuScreen] Updating image from adventure: ${image.substring(0, 50)}...`);
      setImageData(image);
    } else {
      console.log('[GameMenuScreen] adventure has no image');
      setImageData(null);
    }
  }, [localAdventure?.image, currentAdventure?.image]);

  // Animation "garage door" (slide down + fade in) dès le montage du composant
  // Pour que le menu apparaisse immédiatement par-dessus la liste des aventures
  useEffect(() => {
    if (!isClosing) {
      console.log('[GameMenuScreen] Starting garage door animation (opening)');
      // Démarrer l'animation immédiatement, même pendant le chargement
      // Le menu descend depuis le haut, l'overlay devient progressivement opaque
      Animated.parallel([
        Animated.timing(garageDoorAnim, {
          toValue: 0, // Slide down from -height to 0
          duration: 800, // Animation plus lente
          easing: Easing.out(Easing.cubic),
          useNativeDriver: true,
        }),
        Animated.timing(overlayOpacity, {
          toValue: 1, // Overlay fade in from 0 to 1 pour assombrir progressivement
          duration: 800, // Animation plus lente
          easing: Easing.out(Easing.cubic),
          useNativeDriver: true,
        }),
        // garageDoorOpacity reste à 1 (menu toujours visible)
      ]).start();
    }
  }, [isClosing]); // Déclencher dès le montage, pas seulement après le chargement

  // Animation inverse (slide up + fade out) quand isClosing devient true
  useEffect(() => {
    if (isClosing) {
      console.log('[GameMenuScreen] Starting garage door animation (closing)');
      Animated.parallel([
        Animated.timing(garageDoorAnim, {
          toValue: -Dimensions.get('window').height, // Slide up to -height
          duration: 600,
          easing: Easing.in(Easing.cubic),
          useNativeDriver: true,
        }),
        Animated.timing(overlayOpacity, {
          toValue: 0, // Overlay fade out from 1 to 0 pour révéler la liste
          duration: 600,
          easing: Easing.in(Easing.cubic),
          useNativeDriver: true,
        }),
        // garageDoorOpacity reste à 1 jusqu'à la fin
      ]).start(() => {
        console.log('[GameMenuScreen] Animation finished, navigating back');
        // Navigation après que l'animation soit terminée
        // Utiliser goBack() pour revenir à l'écran précédent (Main)
        // cela permet à la liste des aventures de rester visible en arrière-plan
        navigation.goBack();
      });
    }
  }, [isClosing, navigation]);

  const loadMenuData = async () => {
    if (!params.adventureURI) {
      console.error('[GameMenuScreen] No adventureURI provided');
      return;
    }

    try {
      setLoading(true);
      const adventureURI = params.adventureURI;

      // Initialiser les slots par défaut (même sans sauvegarde)
      const defaultSlots: SlotState[] = [1, 2, 3].map(() => ({
        hasSave: false,
        gameState: null,
        dateStr: 'Destin inexploré',
      }));
      setSlots(defaultSlots);

      // Charger l'aventure pour avoir l'image et le nom (SANS initialiser le jeu)
      // Ne pas appeler initGame ici car cela créerait un gameState et un currentStep,
      // ce qui empêcherait le menu de s'afficher dans PlayScreen
      let loadedAdventure: PlayerAdventure | null = null;
      if (!currentAdventure || currentAdventure.uniqueURI !== adventureURI) {
        // Charger uniquement l'aventure (pas le gameState ni le step)
        loadedAdventure = await adventureService.loadPlayerAdventure(adventureURI);
        setLocalAdventure(loadedAdventure);
        // Note: L'image sera mise à jour via le useEffect qui écoute localAdventure.image
      } else {
        setLocalAdventure(currentAdventure);
        loadedAdventure = currentAdventure;
      }

      // Charger les états des 3 slots
      const finalAdventureURI = adventureURI || loadedAdventure?.uniqueURI || '';
      if (!finalAdventureURI) {
        console.error('[GameMenuScreen] No adventureURI available');
        setLoading(false);
        return;
      }

      console.log(`[GameMenuScreen] Loading slots for adventure: ${finalAdventureURI}`);

      const slotPromises = [1, 2, 3].map(async (slotNum) => {
        try {
          const gameState = await gameStateService.getGameStateByAdventureAndSlot(
            finalAdventureURI,
            slotNum,
          );

          if (gameState) {
            const date = new Date(gameState.lastUpdate);
            // Format narratif : "Dernière trace : jour mois année à HH:MM"
            const moisNoms = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
            const heures = date.getHours().toString().padStart(2, '0');
            const minutes = date.getMinutes().toString().padStart(2, '0');
            const dateStr = `Dernière trace : ${date.getDate()} ${moisNoms[date.getMonth()]} ${date.getFullYear()} à ${heures}:${minutes}`;
            return {
              hasSave: true,
              gameState,
              dateStr,
            };
          }

          return {
            hasSave: false,
            gameState: null,
            dateStr: 'Destin inexploré',
          };
        } catch (error) {
          console.error(`[GameMenuScreen] Error loading slot ${slotNum}:`, error);
          return {
            hasSave: false,
            gameState: null,
            dateStr: 'Destin inexploré',
          };
        }
      });

      const loadedSlots = await Promise.all(slotPromises);
      console.log(`[GameMenuScreen] Loaded ${loadedSlots.length} slots:`, loadedSlots.map((s) => ({hasSave: s.hasSave, dateStr: s.dateStr})));
      setSlots(loadedSlots);
    } catch (error) {
      console.error('[GameMenuScreen] Error loading menu data:', error);
      // En cas d'erreur, garder les slots par défaut
    } finally {
      setLoading(false);
    }
  };

  const handleSlotPress = async (slotIndex: number) => {
    console.log(`[GameMenuScreen] ===== handleSlotPress START =====`);
    console.log(`[GameMenuScreen] Slot index: ${slotIndex}, slot number: ${slotIndex + 1}`);
    
    const adventureURI = params.adventureURI || currentAdventure?.uniqueURI || localAdventure?.uniqueURI || '';
    console.log(`[GameMenuScreen] adventureURI: ${adventureURI}`);
    console.log(`[GameMenuScreen] params.adventureURI: ${params.adventureURI}`);
    console.log(`[GameMenuScreen] currentAdventure?.uniqueURI: ${currentAdventure?.uniqueURI}`);
    console.log(`[GameMenuScreen] localAdventure?.uniqueURI: ${localAdventure?.uniqueURI}`);
    
    if (!adventureURI) {
      console.error('[GameMenuScreen] No adventureURI available for slot press');
      Alert.alert('Erreur', 'Impossible de trouver l\'aventure. Veuillez réessayer.');
      return;
    }

    try {
      // Toujours appeler initGame pour s'assurer que currentAdventure est chargé dans le contexte
      // initGame va charger l'aventure dans le contexte PlayerContext via setCurrentAdventure
      console.log(`[GameMenuScreen] Step 1: Initializing game for ${adventureURI}, slot ${slotIndex + 1}`);
      await initGame(adventureURI, slotIndex + 1);
      console.log(`[GameMenuScreen] Step 1: initGame completed`);

      // Passer adventureURI directement à startSlot pour éviter le problème de timing
      // avec currentAdventure qui n'est pas encore mis à jour dans le contexte
      console.log(`[GameMenuScreen] Step 2: Starting slot ${slotIndex + 1} with adventureURI: ${adventureURI}`);
      await startSlot(slotIndex + 1, adventureURI);
      console.log(`[GameMenuScreen] Step 2: startSlot completed`);
      
      // Programmer la navigation pour qu'elle se déclenche quand le contexte sera prêt
      // Le useEffect surveillant isContextReady déclenchera la navigation automatiquement
      console.log(`[GameMenuScreen] Step 3: Scheduling navigation (will trigger when context is ready)`);
      setPendingNavigation({
        adventureURI,
        slot: slotIndex + 1,
      });
      
      // Si le contexte est déjà prêt, la navigation se déclenchera immédiatement via le useEffect
      // Sinon, elle attendra que isContextReady devienne true
      if (isContextReady) {
        console.log(`[GameMenuScreen] Context already ready, navigation will trigger immediately`);
      } else {
        console.log(`[GameMenuScreen] Waiting for context to be ready...`);
      }
      
      console.log(`[GameMenuScreen] ===== handleSlotPress SUCCESS =====`);
    } catch (error: any) {
      console.error(`[GameMenuScreen] ===== handleSlotPress ERROR =====`);
      console.error(`[GameMenuScreen] Error starting slot ${slotIndex + 1}:`, error);
      console.error(`[GameMenuScreen] Error stack:`, error.stack);
      Alert.alert('Erreur', `Impossible de démarrer la partie : ${error.message || error}`);
    }
  };

  const handleCredits = () => {
    navigation.navigate('Credits' as never);
  };

  const handleHallOfFame = () => {
    navigation.navigate('HallOfFame' as never);
  };

  const handleBack = () => {
    console.log('[GameMenuScreen] handleBack called');
    // Démarrer l'animation de fermeture
    setIsClosing(true);
    // La navigation se fera dans le useEffect après l'animation
  };

  const handleDeleteSlot = (slotIndex: number) => {
    console.log(`[GameMenuScreen] handleDeleteSlot called for slot ${slotIndex + 1}`);
    // Ouvrir la modale de confirmation
    setDeleteConfirmationSlot(slotIndex);
  };

  const confirmDeleteSlot = async () => {
    if (deleteConfirmationSlot === null) return;

    const slotIndex = deleteConfirmationSlot;
    const adventureURI = params.adventureURI || currentAdventure?.uniqueURI || localAdventure?.uniqueURI || '';
    console.log(`[GameMenuScreen] confirmDeleteSlot - slot ${slotIndex + 1}, adventureURI: ${adventureURI}`);
    
    if (!adventureURI) {
      console.error('[GameMenuScreen] No adventureURI available for delete');
      setDeleteConfirmationSlot(null);
      // Afficher une erreur (on pourrait aussi créer une modal d'erreur)
      return;
    }

    try {
      await gameStateService.deleteGameState(adventureURI, slotIndex + 1);
      // Recharger les slots
      await loadMenuData();
      setDeleteConfirmationSlot(null);
    } catch (error: any) {
      console.error(`[GameMenuScreen] Error deleting slot ${slotIndex + 1}:`, error);
      setDeleteConfirmationSlot(null);
      // Afficher une erreur (on pourrait aussi créer une modal d'erreur)
    }
  };

  const cancelDeleteSlot = () => {
    setDeleteConfirmationSlot(null);
  };

  const buildImageUri = (imageData?: string): string | null => {
    if (!imageData) return null;
    if (imageData.startsWith('data:image')) {
      return imageData;
    }
    return `data:image/png;base64,${imageData}`;
  };

  // Obtenir le texte narratif du statut d'une destinée
  const getNarrativeStatus = (gameState: GameState | null): string => {
    if (!gameState) return 'Toujours en vie';
    
    // Terminé si le gameState est en SUCCEED
    if (gameState.state === 'SUCCEED') {
      return 'Le sort est scellé';
    }
    
    // Critique si PV <= 25% du max ou PV <= 5 (si pas de max)
    const extraFields = gameState.data.adventureExtraFields || [];
    const pvField = extraFields.find((f: any) => f.type === 'D6_PV');
    
    if (pvField && pvField.currentValue) {
      const currentPV = typeof pvField.currentValue === 'string' 
        ? parseInt(pvField.currentValue, 10) 
        : pvField.currentValue;
      const initPV = pvField.initValue 
        ? (typeof pvField.initValue === 'string' ? parseInt(pvField.initValue, 10) : pvField.initValue)
        : null;
      
      // Si on a une valeur initiale, calculer le pourcentage
      if (initPV && currentPV <= initPV * 0.25) {
        return 'La mort rôde';
      }
      // Sinon, si PV <= 5, considérer comme critique
      if (!initPV && currentPV <= 5) {
        return 'La mort rôde';
      }
    }
    
    return 'Toujours en vie';
  };

  // Utiliser localAdventure, currentAdventure du contexte, ou params.adventureName
  const adventure = localAdventure || currentAdventure;
  const adventureName = adventure?.name || params.adventureName || 'Aventure';

  // Ne plus afficher un écran de chargement séparé - tout passe par l'animation
  // Le menu s'affiche par-dessus la liste dès le début (même pendant le chargement)

  return (
    <SafeAreaView style={[styles.container, styles.transparentContainer]}>
      <Animated.View
        style={[
          styles.backgroundContainer,
          {
            opacity: garageDoorOpacity,
            transform: [
              {
                translateY: garageDoorAnim,
              },
            ],
            // Le menu est maintenant visible dès le début (opacity: 1)
            // et descend depuis le haut (translateY: -height → 0)
          },
        ]}
        pointerEvents="box-none">
        {/* Image de fond */}
        {imageData ? (
          <Image
            source={{uri: imageData}}
            style={styles.backgroundImage}
            resizeMode="cover"
          />
        ) : (
          // Pas de placeholder - laisser la liste visible en arrière-plan si pas d'image
          null
        )}

        {/* Overlay sombre pour la lisibilité - commence transparent pour voir la liste, devient opaque avec l'animation */}
        <Animated.View style={[styles.overlay, {opacity: overlayOpacity}]} pointerEvents="none" />

        {/* Bouton retour (croix) en haut à droite - discret */}
        <TouchableOpacity 
          style={styles.closeButton} 
          onPress={handleBack}
          activeOpacity={0.7}>
          <View style={styles.closeButtonContainer}>
            <FontAwesomeIcon icon={faTimes} size={22} color="rgba(255,255,255,0.8)" />
          </View>
        </TouchableOpacity>

        {/* Titre dans le header fixe en haut */}
        <View style={[styles.headerContainer, {paddingTop: insets.top + 16}]}>
          <View style={styles.titleContainer}>
            <FontAwesomeIcon icon={faBook} size={28} color="#fff" />
            <Text style={styles.title}>{adventureName}</Text>
          </View>
          <Text style={styles.ambianceText}>Choisissez votre destinée.</Text>
        </View>

        {/* Contenu du menu */}
        <ScrollView
          contentContainerStyle={styles.contentContainer}
          style={styles.scrollView}>
          {/* Afficher un spinner pendant le chargement, mais intégré dans l'animation */}
          {loading && (
            <View style={styles.loadingOverlay}>
              <ActivityIndicator size="large" color="#fff" />
              <Text style={styles.loadingText}>Chargement...</Text>
            </View>
          )}

          {/* Espace flexible */}
          <View style={styles.spacer} />

          {/* Slots */}
          <View style={styles.slotsContainer}>
            {slots.length === 0 ? (
              // Si slots pas encore chargés, afficher 3 slots par défaut (CTA)
              [1, 2, 3].map((slotNum) => (
                <View key={slotNum} style={styles.slotWrapper}>
                  <TouchableOpacity
                    style={styles.slotCTA}
                    activeOpacity={0.96}
                    onPress={() => handleSlotPress(slotNum - 1)}>
                    <View style={styles.slotContent}>
                      <Text style={styles.slotCTATitle}>Oser un autre chemin…</Text>
                      <Text style={styles.slotCTASubtext}>Une page est encore vierge</Text>
                    </View>
                  </TouchableOpacity>
                </View>
              ))
            ) : (
              slots.map((slot, index) => {
                const slotNum = index + 1;
                const objectsCount = slot.gameState?.data.objects?.length || 0;
                const skillsCount = slot.gameState?.data.skills?.length || 0;
                const currentStep = slot.gameState?.data.currentStepNumber;
                
                // Récupérer l'avatar
                const avatarImage = slot.gameState?.data.layerAdventurerId;
                const avatarUri = buildImageUri(avatarImage);
                
                // Récupérer les extraFields du GAMESTATE (valeurs actuelles de la partie)
                const gameStateExtraFields = slot.gameState?.data.adventureExtraFields || [];
                
                // Récupérer le système de jeu pour le renderer personnalisé
                // Essayer d'abord avec le type de l'aventure, puis avec 'D6' si des extraFields D6 sont présents
                const adventureForSystem = localAdventure || currentAdventure;
                let adventureSystem = (adventureForSystem as any)?.system || adventureForSystem?.type || 'SIMPLE';
                if (gameStateExtraFields.some((f: any) => f.type?.startsWith('D6_') || f.typeName?.startsWith('D6_'))) {
                  adventureSystem = 'D6';
                }
                const slotRenderer = GameRegistry.getSlotRenderer(adventureSystem);
                const customSlotContent = slotRenderer ? slotRenderer(slot.gameState, index) : null;

                return (
                  <View
                    key={index}
                    style={styles.slotWrapper}
                    {...(Platform.OS === 'web' && {
                      onMouseEnter: () => setHoveredSlotIndex(index),
                      onMouseLeave: () => setHoveredSlotIndex(null),
                    })}>
                    <View style={styles.slotContainer}>
                      {/* Croix de suppression discrète en haut à droite (seulement pour les sauvegardes) */}
                      {slot.hasSave && (
                        <TouchableOpacity
                          style={styles.deleteButton}
                          onPress={(e) => {
                            e.stopPropagation(); // Empêcher la propagation vers le slot parent
                            handleDeleteSlot(index);
                          }}
                          activeOpacity={0.6}
                          hitSlop={{top: 10, bottom: 10, left: 10, right: 10}}>
                          <FontAwesomeIcon 
                            icon={faTimes} 
                            size={12} 
                            color="rgba(107, 114, 128, 0.5)"
                          />
                        </TouchableOpacity>
                      )}
                      
                      <TouchableOpacity
                        style={styles.slot}
                        activeOpacity={0.96}
                        onPress={() => {
                          console.log(`[GameMenuScreen] Slot ${index + 1} clicked, hasSave: ${slot.hasSave}`);
                          handleSlotPress(index);
                        }}
                        // S'assurer que le TouchableOpacity capture bien les clics
                        hitSlop={{top: 5, bottom: 5, left: 5, right: 5}}>
                      
                      {/* Contenu du slot */}
                      <View style={styles.slotContent} pointerEvents="box-none">
                        <View style={styles.slotHeader}>
                          {/* Avatar */}
                          <View style={styles.slotAvatarContainer}>
                            {avatarUri ? (
                              <Image
                                source={{uri: avatarUri}}
                                style={styles.slotAvatar}
                                resizeMode="cover"
                              />
                            ) : (
                              <View style={styles.slotAvatarPlaceholder}>
                                <FontAwesomeIcon icon={faUserCircle} size={20} color="#8B7355" />
                              </View>
                            )}
                          </View>
                          
                          <View style={styles.slotHeaderText}>
                            <Text style={styles.slotPartieLabel}>Destinée {slotNum}</Text>
                            {slot.hasSave ? (
                              <>
                                <Text style={styles.slotStatusNarrative}>{getNarrativeStatus(slot.gameState)}</Text>
                                <Text style={styles.slotSubtext}>{slot.dateStr}</Text>
                                {currentStep && (
                                  <Text style={styles.slotStep}>À l'étape {currentStep}</Text>
                                )}
                                {/* Affichage du chapterTitle ou storyTeaser (optionnel) */}
                                {(() => {
                                  const chapterTitle = slot.gameState?.data.chapterTitle;
                                  const storyTeaser = slot.gameState?.data.storyTeaser;
                                  if (chapterTitle) {
                                    return <Text style={styles.slotNarrative}>{chapterTitle}</Text>;
                                  } else if (storyTeaser) {
                                    return <Text style={styles.slotNarrative}>{storyTeaser}</Text>;
                                  }
                                  return null;
                                })()}
                              </>
                            ) : (
                              <Text style={styles.slotSubtext}>Une page est encore vierge…</Text>
                            )}
                          </View>
                        </View>
                        
                        {slot.hasSave && (
                          <>
                            {/* Rendu personnalisé par système de jeu (ex: D6 avec icônes) */}
                            {customSlotContent}
                            
                            {/* ExtraFields génériques - Format ligne de carnet (si pas de renderer personnalisé) */}
                            {/* Afficher les extraFields du GAMESTATE (valeurs actuelles) */}
                            {!customSlotContent && gameStateExtraFields.length > 0 && (
                              <View style={styles.statsNotebookContainer}>
                                {gameStateExtraFields.slice(0, 4).map((field: any, idx: number) => {
                                  // Le type contient le nom du champ (ex: "D6_FORCE", "D6_PV")
                                  // Nettoyer pour afficher un nom lisible
                                  const fieldType = field.type || field.typeName || '';
                                  const fieldName = fieldType
                                    .replace('D6_', '')
                                    .replace(/_/g, ' ')
                                    .toLowerCase()
                                    .replace(/\b\w/g, (l: string) => l.toUpperCase()) || 'Stat';
                                  // Afficher la currentValue (valeur actuelle de la partie)
                                  const fieldValue = field.currentValue || '';
                                  return (
                                    <Text key={idx} style={styles.statsNotebookLine}>
                                      {fieldName} : {fieldValue}
                                    </Text>
                                  );
                                })}
                              </View>
                            )}
                            
                            {/* Compteurs d'objets et compétences */}
                            <View style={styles.slotCounters}>
                              <View style={styles.counter}>
                                <FontAwesomeIcon
                                  icon={faShoppingBag}
                                  size={14}
                                  color="rgba(0,0,0,0.5)"
                                />
                                <Text style={styles.counterText}>{objectsCount}</Text>
                              </View>
                              <View style={styles.counter}>
                                <FontAwesomeIcon icon={faStar} size={14} color="rgba(0,0,0,0.5)" />
                                <Text style={styles.counterText}>{skillsCount}</Text>
                              </View>
                            </View>
                          </>
                        )}
                      </View>
                    </TouchableOpacity>
                    </View>
                  </View>
                );
              })
            )}
          </View>


          {/* Espace flexible */}
          <View style={styles.spacer} />

          {/* Boutons Crédits et Hall of Fame */}
          <View style={styles.actionsContainer}>
            <TouchableOpacity style={styles.actionButton} onPress={handleCredits}>
              <FontAwesomeIcon icon={faScroll} size={20} color="#fff" style={styles.actionIcon} />
              <Text style={styles.actionButtonText}>Crédits</Text>
            </TouchableOpacity>

            <View style={styles.actionSpacer} />

            <TouchableOpacity style={styles.actionButton} onPress={handleHallOfFame}>
              <FontAwesomeIcon
                icon={faTrophy}
                size={20}
                color="#ffd700"
                style={styles.actionIcon}
              />
              <Text style={[styles.actionButtonText, styles.hallOfFameText]}>Hall of Fame</Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </Animated.View>

      {/* Modal de confirmation de suppression */}
      <Modal
        visible={deleteConfirmationSlot !== null}
        transparent
        animationType="fade"
        onRequestClose={cancelDeleteSlot}>
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={cancelDeleteSlot}>
          <View style={styles.modalContainer} onStartShouldSetResponder={() => true}>
            <Text style={styles.modalTitle}>Briser cette destinée ?</Text>
            <Text style={styles.modalMessage}>
              Cette vie sera effacée à jamais. Aucun retour en arrière ne sera possible.
            </Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={[styles.modalButton, styles.modalButtonCancel]}
                onPress={cancelDeleteSlot}
                activeOpacity={0.7}>
                <Text style={styles.modalButtonTextCancel}>Annuler</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, styles.modalButtonConfirm]}
                onPress={confirmDeleteSlot}
                activeOpacity={0.7}>
                <Text style={styles.modalButtonTextConfirm}>Briser cette destinée</Text>
              </TouchableOpacity>
            </View>
          </View>
        </TouchableOpacity>
      </Modal>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'transparent', // Transparent pour laisser voir la liste en arrière-plan
  },
  transparentContainer: {
    backgroundColor: 'transparent', // Force la transparence
  },
  backgroundContainer: {
    flex: 1,
    position: 'relative',
    backgroundColor: 'transparent', // Transparent pour laisser voir la liste en arrière-plan
  },
  backgroundImage: {
    ...StyleSheet.absoluteFillObject,
    width: '100%',
    height: '100%',
  },
  overlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    ...(Platform.OS === 'web'
      ? {
          backgroundImage: 'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.5) 100%)',
        }
      : {}),
  },
  scrollView: {
    flex: 1,
    zIndex: 1,
  },
  headerContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 20,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 56, // Espace pour le bouton de fermeture à droite
    paddingBottom: 16,
  },
  contentContainer: {
    flexGrow: 1,
    paddingHorizontal: 20,
    paddingTop: 120, // Espace pour le header fixe
    paddingBottom: 28,
    zIndex: 1,
    position: 'relative',
  },
  titleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  title: {
    fontSize: 26,
    fontWeight: 'bold',
    color: '#fff',
    letterSpacing: 1,
    marginLeft: 10,
    textAlign: 'center',
  },
  ambianceText: {
    fontSize: 14,
    fontStyle: 'italic',
    color: 'rgba(255, 255, 255, 0.8)',
    textAlign: 'center',
    fontFamily: Fonts.title,
    letterSpacing: 0.3,
  },
  spacer: {
    flex: 1,
    minHeight: 40,
  },
  slotsContainer: {
    alignItems: 'center',
    maxWidth: 900,
    alignSelf: 'center',
    width: '100%',
    paddingHorizontal: 16,
  },
  slotWrapper: {
    width: '100%',
    maxWidth: 420,
    marginBottom: 20,
    position: 'relative',
  },
  slotContainer: {
    position: 'relative',
  },
  deleteButton: {
    position: 'absolute',
    top: 10,
    right: 10,
    zIndex: 100, // zIndex élevé pour être au-dessus du slot
    // Croix discrète sans fond
    justifyContent: 'center',
    alignItems: 'center',
    padding: 4,
    // Augmenter la zone cliquable
    minWidth: 24,
    minHeight: 24,
  },
  slot: {
    // Fiche papier / cartouche de sauvegarde - même couleur pour tous les slots
    backgroundColor: '#F5F3ED', // Beige / papier (clair) - uniforme pour tous
    ...Platform.select({
      web: {
        borderTopLeftRadius: 14,
        borderTopRightRadius: 10,
        borderBottomRightRadius: 16,
        borderBottomLeftRadius: 12,
      },
      default: {
        borderRadius: 14,
      },
    }),
    padding: 18,
    width: '100%',
    borderWidth: 1,
    borderColor: '#D4C4B0', // Bordure légèrement plus sombre, mat
    minHeight: 80,
    position: 'relative',
    ...Platform.select({
      web: {
        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: {width: 0, height: 1},
        shadowOpacity: 0.06,
        shadowRadius: 3,
        elevation: 2,
      },
    }),
  },
  slotCTA: {
    backgroundColor: '#F5F3ED', // Même couleur que les slots normaux
    ...Platform.select({
      web: {
        borderTopLeftRadius: 14,
        borderTopRightRadius: 10,
        borderBottomRightRadius: 16,
        borderBottomLeftRadius: 12,
      },
      default: {
        borderRadius: 14,
      },
    }),
    padding: 20,
    width: '100%',
    borderWidth: 1,
    borderColor: '#D4C4B0', // Même bordure que les slots normaux
    borderStyle: 'dashed', // Bordure pointillée pour distinguer le CTA
    minHeight: 100,
    justifyContent: 'center',
    alignItems: 'center',
  },
  slotCTATitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#2C3E50',
    marginBottom: 8,
    fontFamily: Fonts.title,
    letterSpacing: 0.5,
    textAlign: 'center',
  },
  slotCTASubtext: {
    fontSize: 15,
    color: '#6B7280',
    fontStyle: 'italic',
    textAlign: 'center',
    fontFamily: Fonts.reading,
  },
  slotContent: {
    flex: 1,
    justifyContent: 'center',
    minHeight: 80, // Assurer une hauteur minimale pour que toute la zone soit cliquable
  },
  slotHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  slotAvatarContainer: {
    marginRight: 12,
  },
  slotAvatar: {
    width: 48,
    height: 48,
    ...Platform.select({
      web: {
        borderTopLeftRadius: 8,
        borderTopRightRadius: 6,
        borderBottomRightRadius: 10,
        borderBottomLeftRadius: 4,
      },
      default: {
        borderRadius: 8,
      },
    }),
    borderWidth: 1,
    borderColor: '#D4C4B0',
  },
  slotAvatarPlaceholder: {
    width: 48,
    height: 48,
    backgroundColor: '#E8D5C4',
    justifyContent: 'center',
    alignItems: 'center',
    ...Platform.select({
      web: {
        borderTopLeftRadius: 8,
        borderTopRightRadius: 6,
        borderBottomRightRadius: 10,
        borderBottomLeftRadius: 4,
      },
      default: {
        borderRadius: 8,
      },
    }),
    borderWidth: 1,
    borderColor: '#D4C4B0',
  },
  slotHeaderText: {
    flex: 1,
  },
  slotTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  slotPartieLabel: {
    fontSize: 18,
    fontWeight: '600',
    color: '#2C3E50',
    fontFamily: Fonts.title,
    letterSpacing: 0.3,
    flex: 1,
  },
  slotStatusNarrative: {
    fontSize: 13,
    fontStyle: 'italic',
    color: '#8B7355',
    marginTop: 2,
    marginBottom: 4,
    fontFamily: Fonts.reading,
  },
  slotNarrative: {
    fontSize: 13,
    fontStyle: 'italic',
    color: '#6B7280',
    marginTop: 4,
    marginBottom: 4,
    fontFamily: Fonts.reading,
    lineHeight: 18,
  },
  slotSubtext: {
    fontSize: 14,
    color: '#6B7280',
    marginBottom: 4,
    fontStyle: 'italic',
  },
  slotStep: {
    fontSize: 13,
    color: '#8A8A8A',
    marginTop: 4,
    marginBottom: 4,
  },
  statsNotebookContainer: {
    marginTop: 12,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: 'rgba(212, 196, 176, 0.3)',
  },
  statsNotebookLine: {
    fontSize: 12,
    color: '#5A5A5A',
    fontFamily: Fonts.reading,
    lineHeight: 18,
    marginBottom: 4,
    letterSpacing: 0.1,
  },
  slotCounters: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
    gap: 12,
  },
  counter: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  counterText: {
    fontSize: 12,
    color: '#6B7280',
  },
  actionsContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 18,
    borderRadius: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.08)', // Plus discret
  },
  actionIcon: {
    marginRight: 8,
  },
  actionButtonText: {
    fontSize: 16,
    color: 'rgba(255, 255, 255, 0.7)',
    fontWeight: '500',
  },
  hallOfFameText: {
    color: '#ffd700',
  },
  actionSpacer: {
    width: 16,
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    backgroundColor: 'rgba(0, 0, 0, 0.3)', // Overlay semi-transparent pour le chargement
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#fff',
  },
  closeButton: {
    position: 'absolute',
    top: 16,
    right: 16,
    zIndex: 1000, // zIndex élevé pour s'assurer que le bouton est cliquable
  },
  closeButtonContainer: {
    width: 36, // Plus petit
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(0, 0, 0, 0.3)', // Opacité réduite
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContainer: {
    backgroundColor: '#F5F3ED',
    borderRadius: 16,
    padding: 24,
    minWidth: 300,
    maxWidth: 400,
    ...Platform.select({
      web: {
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: {width: 0, height: 4},
        shadowOpacity: 0.2,
        shadowRadius: 8,
        elevation: 8,
      },
    }),
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#2C3E50',
    marginBottom: 12,
    textAlign: 'center',
    fontFamily: Fonts.title,
  },
  modalMessage: {
    fontSize: 15,
    color: '#5A5A5A',
    marginBottom: 24,
    textAlign: 'center',
    lineHeight: 22,
    fontFamily: Fonts.reading,
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
  },
  modalButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalButtonCancel: {
    backgroundColor: '#E0E0E0',
  },
  modalButtonConfirm: {
    backgroundColor: '#8B4513',
  },
  modalButtonTextCancel: {
    fontSize: 15,
    color: '#2C3E50',
    fontWeight: '600',
    fontFamily: Fonts.reading,
  },
  modalButtonTextConfirm: {
    fontSize: 15,
    color: '#F5F3ED',
    fontWeight: '600',
    fontFamily: Fonts.reading,
  },
});

export default GameMenuScreen;
